import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FormField from '../../../components/common/form-field';
import readInputCss from '../../../components/form/elements/read-input.module.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faSquare, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FormComponent, FormComponentProps } from '../../../components/form';
import {
  faCheckSquare,
  faChevronDown,
  faChevronRight,
  faMinusSquare,
} from '@fortawesome/pro-solid-svg-icons';
import css from './index.module.css';
import checkboxCss from '../../../components/form/elements/checkbox.module.css';
import { useFormContext } from 'react-hook-form';
import { parseError } from '../../../services/utils';
import { useService } from '../../../services/services';
import OptionModal from '../../../components/modals/option-modal';
import { default as AbilityInterface } from '../../../interfaces/ability';
import i18n from 'i18next';
import { AbilityGroupCheckState } from '../../../enums/ability-group-check-state';

export default function Abilities(
  groupedAbilities: AbilityGroup[],
  selectedAbilities: SelectedAbilities,
  setSelectedAbilities?: (selectedAbilities: SelectedAbilities) => void
): FormComponent {
  return ({ ...rest }) => (
    <AbilitiesComponent
      groupedAbilities={groupedAbilities}
      selectedAbilities={selectedAbilities}
      setSelectedAbilities={setSelectedAbilities}
      {...rest}
    />
  );
}

export type SelectedAbilities = { [key: string]: boolean };
export type SimpleAbility = { id: string; title: string; selected?: boolean };

export type AbilityGroup = {
  title: string;
  children: (AbilityGroup | SimpleAbility)[];
  selectedState: AbilityGroupCheckState;
};

const collectAbilities = (
  abilityGroup:
    | (AbilityGroup | SimpleAbility)
    | (AbilityGroup | SimpleAbility)[]
): SimpleAbility[] => {
  const abilities: SimpleAbility[] = [];

  if (Array.isArray(abilityGroup)) {
    abilityGroup.forEach((group) => {
      abilities.push(...collectAbilities(group));
    });
  } else {
    if (abilityGroup.hasOwnProperty('id')) {
      abilities.push(abilityGroup as unknown as SimpleAbility);
    } else {
      (abilityGroup as AbilityGroup).children.forEach((groupOrAbility) => {
        abilities.push(...collectAbilities(groupOrAbility));
      });
    }
  }

  return abilities;
};

const buildAbilityGroups = (
  abilityGroup:
    | (AbilityGroup | SimpleAbility)
    | (AbilityGroup | SimpleAbility)[],
  selected: SelectedAbilities
): (AbilityGroup | SimpleAbility)[] => {
  const groups: (AbilityGroup | SimpleAbility)[] = [];

  if (Array.isArray(abilityGroup)) {
    abilityGroup.forEach((abilityGroup) => {
      groups.push(...buildAbilityGroups(abilityGroup, selected));
    });
  } else {
    if (abilityGroup.hasOwnProperty('id')) {
      groups.push({
        ...abilityGroup,
        selected: !!selected[(abilityGroup as SimpleAbility).id],
      });
    } else {
      const group: AbilityGroup = {
        title: abilityGroup.title,
        children: buildAbilityGroups(
          (abilityGroup as AbilityGroup).children,
          selected
        ),
        selectedState: AbilityGroupCheckState.NONE,
      };

      group.selectedState = group.children.every(
        (value) =>
          (value as AbilityGroup).selectedState ===
            AbilityGroupCheckState.ALL || (value as SimpleAbility).selected
      )
        ? AbilityGroupCheckState.ALL
        : group.children.some(
            (value) =>
              ((value as AbilityGroup).selectedState &&
                (value as AbilityGroup).selectedState !==
                  AbilityGroupCheckState.NONE) ||
              (value as SimpleAbility).selected
          )
        ? AbilityGroupCheckState.PARTIAL
        : AbilityGroupCheckState.NONE;

      groups.push(group);
    }
  }

  return groups;
};

export function AbilitiesComponent({
  groupedAbilities,
  selectedAbilities,
  readonly,
  setSelectedAbilities = () => null,
}: FormComponentProps & {
  groupedAbilities: AbilityGroup[];
  selectedAbilities: SelectedAbilities;
  setSelectedAbilities?: (selectedAbilities: SelectedAbilities) => void;
}) {
  const { t } = useTranslation();
  const {
    formState: { errors },
  } = useFormContext();
  const [groups, setGroups] = useState([]);
  const error = parseError('selectedAbilities', errors);

  const [selected, setSelected] = useState(selectedAbilities);
  const setSelectedStateAbilities = useCallback(
    (selectedAbilities: SelectedAbilities) => {
      setSelected({ ...selected, ...selectedAbilities });
    },
    [setSelected, selected]
  );
  useEffect(() => {
    setSelectedAbilities(selected);
  }, [setSelectedAbilities, selected]);

  useEffect(() => {
    setGroups(buildAbilityGroups(groupedAbilities, selected));
  }, [groupedAbilities, selected, setGroups]);

  return (
    <FormField
      label={t('pages.farm.users.rights')}
      labelFor={null}
      error={error ? error.message : null}
    >
      <ul className={css['right-list']}>
        {groups.map(({ title, children, selectedState }) => (
          <Group
            key={title}
            name={title}
            groupedAbilities={children}
            selectedAbilities={selected}
            selectedState={selectedState}
            readonly={readonly}
            setSelectedAbilities={setSelectedStateAbilities}
          />
        ))}
      </ul>
    </FormField>
  );
}

function Group({
  name,
  groupedAbilities,
  selectedAbilities,
  readonly,
  selectedState,
  parentName = '',
  setSelectedAbilities,
}: {
  name: string;
  groupedAbilities: (AbilityGroup | SimpleAbility)[];
  selectedAbilities: SelectedAbilities;
  readonly: boolean;
  selectedState: AbilityGroupCheckState;
  parentName?: string;
  setSelectedAbilities: (setSelectedAbilities: SelectedAbilities) => void;
}) {
  const { t } = useTranslation();
  const [modal, setModal] = useState(null);
  const { abilityService } = useService();
  const groupName = parentName ? parentName + '.' + name : name;

  const [open, setOpenState] = useState(true);
  const toggleOpenState = useCallback(
    () => setOpenState(!open),
    [open, setOpenState]
  );

  const onClick = useCallback(() => {
    const checked = selectedState === AbilityGroupCheckState.NONE;

    const abilietiesIds: string[] = collectAbilities(groupedAbilities).map(
      (ability: SimpleAbility) => ability.id
    );

    const abilietiesTitles: string[] = collectAbilities(groupedAbilities).map(
      (ability: SimpleAbility) => ability.title
    );

    const abilities = collectAbilities(groupedAbilities).reduce(function (
      map: any,
      ability: SimpleAbility
    ) {
      map[ability.id] = checked;
      return map;
    },
    {});

    if (checked) {
      abilityService
        .dependsOn(abilietiesIds)
        .subscribe((dependencies: AbilityInterface[]) => {
          DependencyConfirmationModal(
            () =>
              dependencies
                .filter(
                  (ability: AbilityInterface) =>
                    !abilietiesTitles.includes(ability.title)
                )
                .filter(
                  (ability: AbilityInterface) =>
                    !Object.keys(selectedAbilities)
                      .filter((key) => selectedAbilities[key])
                      .includes(ability.id)
                ),
            checked,
            groupName,
            abilities,
            setSelectedAbilities,
            setModal,
            t
          );
        });
    } else {
      abilityService
        .isDependencyOf(abilietiesIds)
        .subscribe((dependencies: AbilityInterface[]) => {
          DependencyConfirmationModal(
            () =>
              dependencies
                .filter(
                  (ability: AbilityInterface) =>
                    !abilietiesTitles.includes(ability.title)
                )
                .filter((ability: AbilityInterface) =>
                  Object.keys(selectedAbilities)
                    .filter((key) => selectedAbilities[key])
                    .includes(ability.id)
                ),
            checked,
            groupName,
            abilities,
            setSelectedAbilities,
            setModal,
            t
          );
        });
    }
  }, [
    selectedState,
    groupedAbilities,
    setSelectedAbilities,
    abilityService,
    groupName,
    selectedAbilities,
    t,
  ]);

  let children = groupedAbilities.map((value: AbilityGroup | SimpleAbility) => {
    if (value.hasOwnProperty('id')) {
      return (
        <Ability
          key={(value as SimpleAbility).id}
          id={(value as SimpleAbility).id}
          title={value.title}
          selected={(value as SimpleAbility).selected}
          readonly={readonly}
          selectedAbilities={selectedAbilities}
          setSelectedAbilities={setSelectedAbilities}
        />
      );
    } else {
      return (
        <Group
          key={value.title}
          parentName={groupName + '_group'}
          name={value.title}
          groupedAbilities={(value as AbilityGroup).children}
          selectedAbilities={selectedAbilities}
          selectedState={(value as AbilityGroup).selectedState}
          readonly={readonly}
          setSelectedAbilities={setSelectedAbilities}
        />
      );
    }
  });

  return (
    <li className={open ? css['opened'] : null} data-group={groupName}>
      <div className={css['title']}>
        <FontAwesomeIcon
          onClick={toggleOpenState}
          className={css['chevron']}
          icon={open ? faChevronDown : faChevronRight}
        />
        {!readonly && (
          <FontAwesomeIcon
            onClick={onClick}
            className={css['check']}
            icon={
              selectedState === AbilityGroupCheckState.ALL
                ? faCheckSquare
                : selectedState === AbilityGroupCheckState.PARTIAL
                ? faMinusSquare
                : faSquare
            }
          />
        )}
        <span>{t(`pages.farm.users.${groupName}`)}</span>
      </div>

      <ul>{children}</ul>
      {modal}
    </li>
  );
}

function Ability({
  id,
  title,
  readonly,
  selected,
  selectedAbilities,
  setSelectedAbilities,
}: SimpleAbility & {
  readonly: boolean;
  selectedAbilities: SelectedAbilities;
  setSelectedAbilities: (setSelectedAbilities: SelectedAbilities) => void;
}) {
  const { t } = useTranslation();
  const { abilityService } = useService();
  const [modal, setModal] = useState(null);
  const onChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;

      const abilities = {
        ...selectedAbilities,
        ...{ [event.target.id]: checked },
      };

      if (checked) {
        abilityService
          .dependsOn(id)
          .subscribe((dependencies: AbilityInterface[]) => {
            DependencyConfirmationModal(
              () =>
                dependencies.filter(
                  (ability: AbilityInterface) =>
                    !Object.keys(selectedAbilities)
                      .filter((key) => selectedAbilities[key])
                      .includes(ability.id)
                ),
              checked,
              'abilities.' + event.target.title,
              abilities,
              setSelectedAbilities,
              setModal,
              t
            );
          });
      } else {
        abilityService
          .isDependencyOf(id)
          .subscribe((dependencies: AbilityInterface[]) => {
            DependencyConfirmationModal(
              () =>
                dependencies.filter((ability: AbilityInterface) =>
                  Object.keys(selectedAbilities)
                    .filter((key) => selectedAbilities[key])
                    .includes(ability.id)
                ),
              checked,
              'abilities.' + event.target.title,
              abilities,
              setSelectedAbilities,
              setModal,
              t
            );
          });
      }
    },
    [id, setSelectedAbilities, abilityService, selectedAbilities, t]
  );

  if (readonly) {
    return (
      <div
        className={`${css['checkboxes-readonly']} ${checkboxCss['readonly']} ${
          readInputCss['readonly']
        } ${checkboxCss[selected ? 'true' : 'false']}`}
      >
        <div>
          {selected ? (
            <FontAwesomeIcon icon={faCheck} data-name={id} />
          ) : (
            <FontAwesomeIcon icon={faTimes} data-name={id} />
          )}
        </div>
        <label htmlFor={id}>{t(`pages.farm.users.abilities.${title}`)}</label>
      </div>
    );
  } else {
    return (
      <div className={css['checkboxes']}>
        <input
          type="checkbox"
          id={id}
          name={id}
          className={checkboxCss['checkbox']}
          onChange={onChange}
          checked={selected}
          title={`${title}`}
        />
        <label htmlFor={id} className="ml-2">
          {t(`pages.farm.users.abilities.${title}`)}
        </label>
        {modal}
      </div>
    );
  }
}

function DependencyConfirmationModal(
  missingDependenciesCallback: () => AbilityInterface[],
  checked: boolean,
  title: string,
  abilities: SelectedAbilities,
  setSelectedAbilities: (setSelectedAbilities: SelectedAbilities) => void,
  setModal: any,
  t: any
): void {
  const missingDependencies = missingDependenciesCallback();

  missingDependencies.map(
    (dependency: AbilityInterface): any => (abilities[dependency.id] = checked)
  );

  const titleList = missingDependencies.map(
    (dependency: AbilityInterface) => dependency.title
  );

  if (titleList.length) {
    const transatedTitle = t('pages.farm.users.' + title);
    const quotedTitle = transatedTitle.endsWith("'")
      ? transatedTitle
      : "''".concat(transatedTitle, "''");

    setModal(
      <OptionModal
        title={i18n.t(
          'pages.farm.users.abilities.' +
            (checked ? 'ability_depends_on' : 'ability_is_dependency_of'),
          {
            ability: quotedTitle,
          }
        )}
        content={
          <div>
            {titleList.map((title: string) => (
              <p key={title}>{t('pages.farm.users.abilities.' + title)}</p>
            ))}
          </div>
        }
        buttons={[
          { title: t('common.ok'), name: 'Ok', value: true },
          { title: t('common.cancel'), name: 'Cancel', value: false },
        ]}
        onChoice={(choice: boolean) => {
          updateAbilities(checked, choice);
          setModal(null);
        }}
        onClose={() => setModal(null)}
      />
    );
  } else {
    updateAbilities(checked, true);
  }

  function updateAbilities(checked: boolean, choice: boolean): void {
    missingDependencies.forEach((ability: AbilityInterface) => {
      abilities[ability.id] = checked;
    });

    if (choice) {
      setSelectedAbilities(abilities);
    }
  }
}
